// OWNER: AREA-OPS (dashboard, applications/*, suppressions, audit, system). Components go in src/features/ops.
// Audit log: a filterable feed of admin changes with before/after diffs. Admins only.
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { ScrollText, Search, ShieldOff, X } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { allApplicationsQuery } from '@/api/queries/applications'
import type { AuditEntry } from '@/api/types'
import { usePermissions } from '@/auth/usePermissions'
import { EmptyState } from '@/components/EmptyState'
import { PageHeader } from '@/components/PageHeader'
import { ErrorState } from '@/components/QueryState'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { SkeletonText } from '@/components/ui/skeleton'
import { useAuditLog } from '@/features/ops/api'
import { AuditEntryRow } from '@/features/ops/AuditEntryRow'
import {
  AUDIT_ENTITY_TYPES,
  entityLabel,
  isTimeWindow,
  windowStart,
  type TimeWindow,
} from '@/features/ops/lib'
import { viaLabel } from '@/api/queries/proposals'
import { formatDate } from '@/lib/format'
import { useDebouncedValue } from '@/lib/useDebouncedValue'
import { useAppScope } from '@/shell/useAppScope'

interface Search {
  actor?: string
  action?: string
  entity_type?: string
  entity_id?: string
  /** The client label changes came through, for example `claude`. */
  via?: string
  window?: TimeWindow
}

const str = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : undefined)

export const Route = createFileRoute('/audit')({
  staticData: { crumb: 'Audit log' },
  validateSearch: (s: Record<string, unknown>): Search => ({
    actor: str(s.actor),
    action: str(s.action),
    entity_type: str(s.entity_type),
    entity_id: str(s.entity_id),
    via: str(s.via),
    window: isTimeWindow(s.window) ? s.window : undefined,
  }),
  component: AuditPage,
})

const WINDOWS = [
  { value: 'any', label: 'Any time' },
  { value: '1h', label: 'Last hour' },
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
] as const

const ACTION_PREFIXES = [
  { value: 'any', label: 'Any action' },
  ...AUDIT_ENTITY_TYPES.map((t) => ({
    value: `${t}.`,
    label: t === 'message' ? 'Message resends and cancels' : `Any ${entityLabel(t)} change`,
  })),
]

/** A text filter that writes to the URL after a short pause. */
function useUrlText(value: string | undefined, write: (v: string | undefined) => void) {
  const [text, setText] = useState(value ?? '')
  const [seen, setSeen] = useState(value)
  // Follow external URL changes (back button, "history of this") without
  // clobbering what is being typed (for example a trailing space).
  if (seen !== value) {
    setSeen(value)
    if (text.trim() !== (value ?? '')) setText(value ?? '')
  }
  const debounced = useDebouncedValue(text.trim(), 300)
  // Write only once the debounce has caught up with the text, so a stale
  // debounced value never overwrites an external change.
  const settled = debounced === text.trim()
  useEffect(() => {
    if (settled && (value ?? '') !== debounced) write(debounced || undefined)
  }, [settled, value, debounced, write])
  return [text, setText] as const
}

function AuditPage() {
  const { can, isLoading } = usePermissions()
  if (!isLoading && !can('view_audit'))
    return (
      <>
        <PageHeader title="Audit log" description="Who changed what, and when." />
        <Card>
          <EmptyState
            icon={ShieldOff}
            title="Only Admins can see the audit log"
            description="Ask a MailSmith admin if you need to know who changed something."
          />
        </Card>
      </>
    )
  // Hold the request until the role is known, so a viewer never sends a 403.
  return <AuditFeed enabled={!isLoading} />
}

function AuditFeed({ enabled }: { enabled: boolean }) {
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const { appId, application } = useAppScope()
  const apps = useQuery(allApplicationsQuery)
  const [expanded, setExpanded] = useState<Set<number>>(() => new Set())

  const set = useMemo(
    () => (patch: Partial<Search>) =>
      void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  )
  const writeActor = useMemo(() => (actor: string | undefined) => set({ actor }), [set])
  const writeEntityId = useMemo(() => (entity_id: string | undefined) => set({ entity_id }), [set])
  const [actorText, setActorText] = useUrlText(search.actor, writeActor)
  const [entityText, setEntityText] = useUrlText(search.entity_id, writeEntityId)

  // Recomputed per filter change only, so the query key stays stable while paging.
  const since = useMemo(
    () => (search.window ? windowStart(search.window) : undefined),
    [search.window],
  )
  const log = useAuditLog(
    {
      app: appId,
      actor: search.actor,
      action: search.action,
      entity_type: search.entity_type,
      entity_id: search.entity_id,
      via: search.via,
      since,
    },
    enabled,
  )

  const names = useMemo(() => new Map((apps.data ?? []).map((a) => [a.id, a.name])), [apps.data])
  const groups = useMemo(() => groupByDay(log.items), [log.items])
  const filtered = !!(
    search.actor ||
    search.action ||
    search.entity_type ||
    search.entity_id ||
    search.via ||
    search.window
  )

  const toggle = (id: number) =>
    setExpanded((s) => {
      const next = new Set(s)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const actionValue = search.action ?? 'any'
  const actionOptions = ACTION_PREFIXES.some((o) => o.value === actionValue)
    ? ACTION_PREFIXES
    : [...ACTION_PREFIXES, { value: actionValue, label: actionValue }]

  return (
    <>
      <PageHeader
        title="Audit log"
        description={
          application
            ? `Changes to ${application.name}, newest first. Entries are kept for two years.`
            : 'Every admin change, newest first. Entries are kept for two years.'
        }
      />
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Input
          className="w-52"
          size="sm"
          placeholder="Actor name or ID"
          aria-label="Filter by actor"
          value={actorText}
          onChange={(e) => setActorText(e.target.value)}
          leading={<Search />}
        />
        <Select
          aria-label="Action"
          size="sm"
          className="w-52"
          value={actionValue}
          onValueChange={(v) => set({ action: v === 'any' ? undefined : v })}
          options={actionOptions}
        />
        <Select
          aria-label="Entity type"
          size="sm"
          className="w-44"
          value={search.entity_type ?? 'any'}
          onValueChange={(v) => set({ entity_type: v === 'any' ? undefined : v })}
          options={[
            { value: 'any', label: 'Any entity' },
            ...AUDIT_ENTITY_TYPES.map((t) => ({ value: t, label: capitalize(entityLabel(t)) })),
          ]}
        />
        <Input
          className="w-56 font-mono"
          size="sm"
          placeholder="Entity ID"
          aria-label="Filter by entity ID"
          value={entityText}
          onChange={(e) => setEntityText(e.target.value)}
        />
        <Select
          aria-label="Time range"
          size="sm"
          className="w-40"
          value={search.window ?? 'any'}
          onValueChange={(v) => set({ window: v === 'any' ? undefined : (v as TimeWindow) })}
          options={WINDOWS}
        />
        {search.via && (
          <Button
            size="sm"
            variant="ghost"
            icon={<X />}
            aria-label={`Stop filtering by ${viaLabel(search.via)}`}
            onClick={() => set({ via: undefined })}
          >
            Through {viaLabel(search.via)}
          </Button>
        )}
        {filtered && (
          <Button
            size="sm"
            variant="ghost"
            icon={<X />}
            onClick={() =>
              set({
                actor: undefined,
                action: undefined,
                entity_type: undefined,
                entity_id: undefined,
                via: undefined,
                window: undefined,
              })
            }
          >
            Clear filters
          </Button>
        )}
      </div>
      <Card className="overflow-hidden">
        {log.isPending ? (
          <div className="p-4">
            <SkeletonText lines={8} />
          </div>
        ) : log.isError ? (
          <ErrorState error={log.error} onRetry={() => void log.refetch()} />
        ) : log.items.length === 0 ? (
          <EmptyState
            icon={ScrollText}
            title={filtered ? 'No matching changes' : 'No changes recorded yet'}
            description={
              filtered
                ? 'Nothing matches these filters. Try a wider time range.'
                : 'Every change made in the admin UI or API shows up here with who made it.'
            }
          />
        ) : (
          <>
            {groups.map(([day, entries]) => (
              <section key={day} aria-label={day}>
                <h2 className="sticky top-0 z-[1] border-b border-line bg-surface-2 px-4 py-1.5 text-xs font-medium text-ink-2">
                  {day}
                </h2>
                <ul className="divide-y divide-line border-b border-line last:border-b-0">
                  {entries.map((e) => (
                    <AuditEntryRow
                      key={e.id}
                      entry={e}
                      appName={!appId && e.application_id ? names.get(e.application_id) : undefined}
                      expanded={expanded.has(e.id)}
                      onToggle={() => toggle(e.id)}
                      onFilterEntity={(entity_type, entity_id) =>
                        set({ entity_type, entity_id, action: undefined })
                      }
                      onFilterVia={(via) => set({ via })}
                    />
                  ))}
                </ul>
              </section>
            ))}
            <div className="flex items-center justify-between gap-2 border-t border-line bg-surface-2 px-4 py-1.5 text-xs text-ink-2">
              <span className="tabular">
                {log.items.length} {log.items.length === 1 ? 'entry' : 'entries'}
              </span>
              {log.pagination.hasMore && (
                <Button
                  size="xs"
                  onClick={log.pagination.loadMore}
                  loading={log.pagination.isLoadingMore}
                >
                  Load more
                </Button>
              )}
            </div>
          </>
        )}
      </Card>
      {!appId && (
        <p className="mt-2 text-xs text-ink-3">
          Showing every application. Pick one in the top bar to narrow the log.
        </p>
      )}
    </>
  )
}

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

function groupByDay(items: AuditEntry[]): [string, AuditEntry[]][] {
  const out = new Map<string, AuditEntry[]>()
  const today = formatDate(new Date().toISOString())
  const yesterday = formatDate(new Date(Date.now() - 86400_000).toISOString())
  for (const e of items) {
    const d = formatDate(e.created_at)
    const label = d === today ? 'Today' : d === yesterday ? 'Yesterday' : d
    const list = out.get(label)
    if (list) list.push(e)
    else out.set(label, [e])
  }
  return [...out]
}
